import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AutocompleteSuggestion } from '@klareospace/pharos-lib-types';
import { Loader2, MapPin, Search } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { formatSuggestionAddress } from './format-suggestion-address';
import { ScoreBadge } from './score-badge';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressSearchInputProps {
  /** Text shown in the popover when a completed search returns zero suggestions. */
  emptyResultsLabel: string;
  /** Label for the "Enter address manually" escape hatch rendered in the empty-results state. Omit together with `onEnterManually` to hide the escape hatch entirely (e.g. signup, which requires a geocoded selection). */
  enterManuallyLabel?: string;
  error?: string;
  isLoading: boolean;
  isSelected?: boolean;
  /** Called when the user activates the empty-results escape hatch. Omit to hide the escape hatch. */
  onEnterManually?: () => void;
  onQueryChange: (query: string) => void;
  onSelect: (suggestion: AutocompleteSuggestion) => void;
  placeholder: string;
  query: string;
  readOnly?: boolean;
  score?: number | null;
  searchAriaLabel: string;
  suggestions: AutocompleteSuggestion[];
}

// ─── Internal ─────────────────────────────────────────────────────────────────

interface SearchFieldProps {
  ariaLabel: string;
  error?: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isLoading: boolean;
  isOpen: boolean;
  isSelected: boolean;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onQueryChange: (query: string) => void;
  placeholder: string;
  query: string;
  readOnly?: boolean;
  score: number | null;
}

function SearchField({
  ariaLabel,
  error,
  inputRef,
  isLoading,
  isOpen,
  isSelected,
  onKeyDown,
  onQueryChange,
  placeholder,
  query,
  readOnly,
  score,
}: SearchFieldProps): React.JSX.Element {
  return (
    <div className="relative">
      <Search className="text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2" />
      <input
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className={cn(
          'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border ps-10 pe-10 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          isSelected &&
            'border-2 border-primary bg-primary/5 focus:ring-0 focus:ring-offset-0 focus:border-primary pr-20',
          readOnly && 'cursor-default bg-muted/40',
          error && 'border-destructive',
        )}
        onChange={(e): void => onQueryChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={inputRef}
        role="combobox"
        type="text"
        value={query}
      />
      {!isSelected && isLoading && (
        <Loader2 className="text-muted-foreground absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin" />
      )}
      {isSelected && score != null && <ScoreBadge score={score} />}
    </div>
  );
}

interface SuggestionListProps {
  activeIndex: number;
  ariaLabel: string;
  onSelect: (suggestion: AutocompleteSuggestion) => void;
  suggestions: AutocompleteSuggestion[];
}

function SuggestionList({ activeIndex, ariaLabel, onSelect, suggestions }: SuggestionListProps): React.JSX.Element {
  return (
    <ul
      aria-label={ariaLabel}
      className="bg-popover text-popover-foreground absolute z-[999] mt-1 max-h-60 w-full overflow-auto rounded-md border shadow-md"
      role="listbox"
    >
      {suggestions.map((suggestion, index) => (
        <li
          aria-selected={index === activeIndex}
          className={cn(
            'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
            index === activeIndex && 'bg-accent text-accent-foreground',
          )}
          key={suggestion.id}
          onMouseDown={(e): void => {
            e.preventDefault();
            onSelect(suggestion);
          }}
          role="option"
        >
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-medium">{formatSuggestionAddress(suggestion.address) || suggestion.title}</span>
        </li>
      ))}
    </ul>
  );
}

interface EmptyResultsProps {
  emptyResultsLabel: string;
  enterManuallyLabel?: string;
  onEnterManually?: () => void;
}

/** Neutral (non-error) escape hatch shown when a completed search returns zero suggestions. */
function EmptyResults({
  emptyResultsLabel,
  enterManuallyLabel,
  onEnterManually,
}: EmptyResultsProps): React.JSX.Element {
  return (
    <div className="bg-popover text-popover-foreground absolute z-[999] mt-1 w-full rounded-md border p-3 shadow-md">
      <p className="text-sm text-muted-foreground">{emptyResultsLabel}</p>
      {onEnterManually && enterManuallyLabel && (
        <button
          className="mt-1 text-sm font-medium text-primary hover:underline"
          onClick={onEnterManually}
          type="button"
        >
          {enterManuallyLabel}
        </button>
      )}
    </div>
  );
}

// ─── Keyboard navigation ──────────────────────────────────────────────────────

interface UseSuggestionKeyboardNavOptions {
  activeIndex: number;
  closeSuggestions: () => void;
  isOpen: boolean;
  onSelect: (suggestion: AutocompleteSuggestion) => void;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  suggestions: AutocompleteSuggestion[];
}

/** Extracted to keep AddressSearchInput's cyclomatic complexity low. */
function useSuggestionKeyboardNav({
  activeIndex,
  closeSuggestions,
  isOpen,
  onSelect,
  setActiveIndex,
  suggestions,
}: UseSuggestionKeyboardNavOptions): (event: React.KeyboardEvent<HTMLInputElement>) => void {
  return useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (!isOpen || suggestions.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        const selected = suggestions[activeIndex];
        if (selected) {
          onSelect(selected);
          closeSuggestions();
        }
        return;
      }
      if (event.key === 'Escape') {
        closeSuggestions();
      }
    },
    [activeIndex, closeSuggestions, isOpen, onSelect, setActiveIndex, suggestions],
  );
}

// ─── Popover mode ─────────────────────────────────────────────────────────────

type PopoverMode = 'empty' | 'none' | 'suggestions';

/** Extracted to keep AddressSearchInput's cyclomatic complexity low. */
function resolvePopoverMode(isExpanded: boolean, isLoading: boolean, suggestionCount: number): PopoverMode {
  if (!isExpanded || isLoading) return 'none';
  return suggestionCount > 0 ? 'suggestions' : 'empty';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AddressSearchInput({
  emptyResultsLabel,
  enterManuallyLabel,
  error,
  isLoading,
  isSelected = false,
  onEnterManually,
  onQueryChange,
  onSelect,
  placeholder,
  query,
  readOnly = false,
  score = null,
  searchAriaLabel,
  suggestions,
}: AddressSearchInputProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeSuggestions = useCallback((): void => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    if (readOnly || isSelected) {
      setIsOpen(false);
      return;
    }
    setIsOpen(query.trim().length > 0 && !isLoading);
    setActiveIndex(-1);
  }, [isLoading, isSelected, query, readOnly]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeSuggestions();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeSuggestions]);

  const handleKeyDown = useSuggestionKeyboardNav({
    activeIndex,
    closeSuggestions,
    isOpen,
    onSelect,
    setActiveIndex,
    suggestions,
  });

  const handleSelect = (suggestion: AutocompleteSuggestion): void => {
    onSelect(suggestion);
    closeSuggestions();
  };

  const handleEnterManually = (): void => {
    closeSuggestions();
    onEnterManually?.();
  };

  const isExpanded = isOpen && !readOnly;
  const popoverMode = resolvePopoverMode(isExpanded, isLoading, suggestions.length);

  return (
    <div className="relative" ref={containerRef}>
      <SearchField
        ariaLabel={searchAriaLabel}
        error={error}
        inputRef={inputRef}
        isLoading={isLoading}
        isOpen={isExpanded}
        isSelected={isSelected}
        onKeyDown={handleKeyDown}
        onQueryChange={onQueryChange}
        placeholder={placeholder}
        query={query}
        readOnly={readOnly}
        score={score}
      />

      {/* Suppressed while the suggestions/empty-results popover is open — it renders in the same spot below the input. */}
      {error && popoverMode === 'none' && <p className="text-destructive mt-1 text-sm">{error}</p>}

      {popoverMode === 'suggestions' && (
        <SuggestionList
          activeIndex={activeIndex}
          ariaLabel={searchAriaLabel}
          onSelect={handleSelect}
          suggestions={suggestions}
        />
      )}

      {popoverMode === 'empty' && (
        <EmptyResults
          emptyResultsLabel={emptyResultsLabel}
          enterManuallyLabel={enterManuallyLabel}
          onEnterManually={handleEnterManually}
        />
      )}
    </div>
  );
}
